// src/components/UserSettings.jsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';

function UserSettings({ currentUser, db, onClose, showToast, onDeleteAccount }) {
  const [settings, setSettings] = useState({
    firstReminder: true,
    secondReminder: true,
    weeklyReminder: true,
    firstReminderTime: '12:00',
    secondReminderTime: '18:00'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [currentUser]);

  const loadSettings = async () => {
    try {
      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'preferences');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSettings({
          firstReminder: data.firstReminder ?? data.noonReminder ?? true,
          secondReminder: data.secondReminder ?? data.eveningReminder ?? true,
          weeklyReminder: data.weeklyReminder ?? true,
          firstReminderTime: data.firstReminderTime ?? data.noonTime ?? '12:00',
          secondReminderTime: data.secondReminderTime ?? data.eveningTime ?? '18:00'
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      if (error.code === 'permission-denied') {
        showToast('⚠️ Cannot load settings. Please check Firebase permissions.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'preferences');
      await setDoc(settingsRef, settings, { merge: true });
      
      showToast('Settings saved successfully!', 'success');
      setTimeout(() => onClose(), 800);
    } catch (error) {
      console.error('Error saving settings:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        showToast('⚠️ Permission denied. You need to update Firebase Firestore Rules.', 'error');
        console.error('🔥 FIREBASE PERMISSION ERROR 🔥');
        console.error('To fix: Firebase Console → Firestore → Rules');
        console.error('Add settings permission under users/{userId}');
      } else {
        showToast('Failed to save settings. Check console for details.', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '30px',
          textAlign: 'center'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '15px',
      zIndex: 1000,
      overflowY: 'auto'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)'
      }}>
        <h2 style={{ 
          fontSize: '22px', 
          fontWeight: 'bold', 
          marginBottom: '15px',
          color: '#1f2937'
        }}>
          ⚙️ Notification Settings
        </h2>

        {/* Daily Reminders Section - SIDE BY SIDE */}
        <div style={{
          marginBottom: '15px',
          padding: '15px',
          background: '#fef3c7',
          borderRadius: '8px',
          border: '2px solid #fcd34d'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '12px',
            color: '#92400e'
          }}>
            📅 Daily Training Reminders
          </h3>

          {/* REMINDERS IN A ROW */}
          <div style={{ 
            display: 'flex', 
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {/* First Reminder */}
            <div style={{ 
              flex: '1 1 180px',
              minWidth: '180px',
              maxWidth: '220px'
            }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                <input
                  type="checkbox"
                  checked={settings.firstReminder}
                  onChange={(e) => setSettings({...settings, firstReminder: e.target.checked})}
                  style={{
                    width: '16px',
                    height: '16px',
                    marginRight: '8px',
                    cursor: 'pointer'
                  }}
                />
                <span>🔔 1st Reminder</span>
              </label>
              {settings.firstReminder && (
                <input
                  type="time"
                  value={settings.firstReminderTime}
                  onChange={(e) => setSettings({...settings, firstReminderTime: e.target.value})}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    padding: '8px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              )}
            </div>

            {/* Second Reminder */}
            <div style={{ 
              flex: '1 1 180px',
              minWidth: '180px',
              maxWidth: '220px'
            }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                <input
                  type="checkbox"
                  checked={settings.secondReminder}
                  onChange={(e) => setSettings({...settings, secondReminder: e.target.checked})}
                  style={{
                    width: '16px',
                    height: '16px',
                    marginRight: '8px',
                    cursor: 'pointer'
                  }}
                />
                <span>🔔 2nd Reminder</span>
              </label>
              {settings.secondReminder && (
                <input
                  type="time"
                  value={settings.secondReminderTime}
                  onChange={(e) => setSettings({...settings, secondReminderTime: e.target.value})}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    padding: '8px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Weekly Summary Section */}
        <div style={{
          marginBottom: '15px',
          padding: '15px',
          background: '#f0fdf4',
          borderRadius: '8px',
          border: '2px solid #86efac'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '10px',
            color: '#166534'
          }}>
            📊 Weekly Summary Reminder
          </h3>

          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            <input
              type="checkbox"
              checked={settings.weeklyReminder}
              onChange={(e) => setSettings({...settings, weeklyReminder: e.target.checked})}
              style={{
                width: '16px',
                height: '16px',
                marginRight: '8px',
                cursor: 'pointer'
              }}
            />
            <span>Sunday Evening (8:00 PM)</span>
          </label>
        </div>

        {/* Info Note */}
        <div style={{
          background: '#eff6ff',
          padding: '10px',
          borderRadius: '6px',
          marginBottom: '15px',
          fontSize: '12px',
          color: '#1e40af',
          border: '1px solid #bfdbfe'
        }}>
          💡 Changes take effect the next day.
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1,
              background: '#d97706',
              color: 'white',
              padding: '10px',
              borderRadius: '8px',
              border: 'none',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '15px',
              fontWeight: '500',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1,
              background: '#f3f4f6',
              color: '#374151',
              padding: '10px',
              borderRadius: '8px',
              border: 'none',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '15px'
            }}
          >
            Cancel
          </button>
        </div>

        {/* Delete Account Section */}
        {onDeleteAccount && (
          <div style={{
            marginTop: '15px',
            paddingTop: '15px',
            borderTop: '2px solid #fee2e2'
          }}>
            <h3 style={{ 
              fontSize: '14px', 
              fontWeight: 'bold', 
              color: '#dc2626',
              marginBottom: '8px' 
            }}>
              ⚠️ Danger Zone
            </h3>
            <p style={{ 
              fontSize: '12px', 
              color: '#6b7280', 
              marginBottom: '10px',
              lineHeight: '1.4'
            }}>
              Permanently delete all your dogs, training entries, questions, and weekly summaries.
            </p>
            <button
              onClick={onDeleteAccount}
              style={{
                background: '#dc2626',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#b91c1c'}
              onMouseOut={(e) => e.currentTarget.style.background = '#dc2626'}
            >
              🗑️ Delete Account
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserSettings;
